import { FuncArr } from './func_arr';
import { isElement } from './type_symbol';
export class MutationObserverTarget {
    constructor(bound) {
        /* @internal */
        this._$subtreeObserversCount = 0;
        this.attrObservers = null;
        this.allAttrObservers = null;
        this.textObservers = null;
        this.childObservers = null;
        this.attachObservers = null;
        this._$bound = bound;
    }
    attachChild(child) {
        if (!child._$mutationObserverTarget) {
            child._$mutationObserverTarget = new MutationObserverTarget(child);
        }
        if (this._$subtreeObserversCount > 0) {
            child._$mutationObserverTarget.updateSubtreeCount(this._$subtreeObserversCount);
        }
    }
    detachChild(child) {
        if (child._$mutationObserverTarget) {
            child._$mutationObserverTarget.updateSubtreeCount(-this._$subtreeObserversCount);
        }
    }
    updateSubtreeCount(diff) {
        this._$subtreeObserversCount += diff;
        const elem = this._$bound;
        if (!isElement(elem))
            return;
        const children = elem.childNodes;
        children.forEach((child) => {
            if (!child._$mutationObserverTarget) {
                child._$mutationObserverTarget = new MutationObserverTarget(child);
            }
            child._$mutationObserverTarget.updateSubtreeCount(diff);
        });
    }
    hasSubtreeListeners() {
        return this._$subtreeObserversCount > 0;
    }
    static callAttrObservers(node, eventObj) {
        var _a;
        let cur = node;
        do {
            const target = cur._$mutationObserverTarget;
            (_a = target === null || target === void 0 ? void 0 : target.allAttrObservers) === null || _a === void 0 ? void 0 : _a.call(cur, [eventObj]);
            if ((target === null || target === void 0 ? void 0 : target.attrObservers) &&
                (eventObj.nameType === 'basic' ||
                    eventObj.nameType === 'attribute' ||
                    eventObj.nameType === 'component-property')) {
                target.attrObservers.call(cur, [eventObj]);
            }
            const next = cur.parentNode;
            if (!next)
                break;
            cur = next;
            if (!cur._$mutationObserverTarget)
                break;
        } while (cur._$mutationObserverTarget._$subtreeObserversCount > 0);
    }
    static callTextObservers(textNode, eventObj) {
        var _a;
        let cur = textNode;
        do {
            const target = cur._$mutationObserverTarget;
            (_a = target === null || target === void 0 ? void 0 : target.textObservers) === null || _a === void 0 ? void 0 : _a.call(cur, [eventObj]);
            const next = cur.parentNode;
            if (!next)
                break;
            cur = next;
            if (!cur._$mutationObserverTarget)
                break;
        } while (cur._$mutationObserverTarget._$subtreeObserversCount > 0);
    }
    static callChildObservers(node, eventObj) {
        var _a;
        let cur = node;
        do {
            const target = cur._$mutationObserverTarget;
            (_a = target === null || target === void 0 ? void 0 : target.childObservers) === null || _a === void 0 ? void 0 : _a.call(cur, [eventObj]);
            const next = cur.parentNode;
            if (!next)
                break;
            cur = next;
            if (!cur._$mutationObserverTarget)
                break;
        } while (cur._$mutationObserverTarget._$subtreeObserversCount > 0);
    }
    static callAttachObservers(node, eventObj) {
        var _a;
        const target = node._$mutationObserverTarget;
        (_a = target === null || target === void 0 ? void 0 : target.attachObservers) === null || _a === void 0 ? void 0 : _a.call(node, [eventObj]);
    }
}
/**
 * An observer that can observe shadow tree changes
 *
 * Like DOM MutationObserver,
 * this observer can observe attributes, text content, and child nodes changes.
 * It can optionally observe changes in a subtree.
 * Further more, it can listen attached/detached events on an element.
 */
export class MutationObserver {
    constructor(listener) {
        /* @internal */
        this._$subtreeListenersCount = 0;
        /* @internal */
        this._$boundFuncArrs = [];
        /* @internal */
        this._$boundTarget = null;
        this._$listener = listener;
        this._$normalizedListener = null;
    }
    static create(listener) {
        return new MutationObserver(listener);
    }
    /** Start observation */
    observe(targetNode, options = {
        properties: false,
        childList: false,
        characterData: false,
        subtree: false,
        attachStatus: false,
    }) {
        const listener = this._$listener;
        if (!listener) {
            throw new Error('A MutationObserver can only observe once');
        }
        let target;
        if (targetNode._$mutationObserverTarget) {
            target = targetNode._$mutationObserverTarget;
        }
        else {
            target = new MutationObserverTarget(targetNode);
            targetNode._$mutationObserverTarget = target;
        }
        this._$listener = null;
        const cb = options.subtree
            ? listener
            : function noSubtreeListener(ev) {
                // this might be TextNode, but do the same as Element
                if (ev.target === this)
                    listener.call(this, ev);
            };
        this._$normalizedListener = cb;
        this._$boundTarget = target;
        if (options.properties === 'all') {
            if (!target.allAttrObservers)
                target.allAttrObservers = new FuncArr('attributeObserver');
            target.allAttrObservers.add(cb);
            this._$boundFuncArrs.push(target.allAttrObservers);
            this._$subtreeListenersCount += 1;
        }
        else if (options.properties) {
            if (!target.attrObservers)
                target.attrObservers = new FuncArr('attributeObserver');
            target.attrObservers.add(cb);
            this._$boundFuncArrs.push(target.attrObservers);
            this._$subtreeListenersCount += 1;
        }
        if (options.childList) {
            if (!target.childObservers)
                target.childObservers = new FuncArr('childObserver');
            target.childObservers.add(cb);
            this._$boundFuncArrs.push(target.childObservers);
            this._$subtreeListenersCount += 1;
        }
        if (options.characterData) {
            if (!target.textObservers)
                target.textObservers = new FuncArr('textObserver');
            target.textObservers.add(cb);
            this._$boundFuncArrs.push(target.textObservers);
            this._$subtreeListenersCount += 1;
        }
        if (options.subtree) {
            target.updateSubtreeCount(this._$subtreeListenersCount);
        }
        if (options.attachStatus) {
            if (!target.attachObservers)
                target.attachObservers = new FuncArr('attachObserver');
            target.attachObservers.add(cb);
            this._$boundFuncArrs.push(target.attachObservers);
        }
    }
    /** End observation */
    disconnect() {
        var _a;
        (_a = this._$boundTarget) === null || _a === void 0 ? void 0 : _a.updateSubtreeCount(-this._$subtreeListenersCount);
        const arr = this._$boundFuncArrs;
        this._$boundFuncArrs = [];
        const nl = this._$normalizedListener;
        if (nl) {
            arr.forEach((funcArr) => {
                funcArr.remove(nl);
            });
        }
    }
}
//# sourceMappingURL=mutation_observer.js.map